import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../api/client';
import { SessionCheck } from '../api/types';
import { useI18n } from '../i18n/I18nContext';

/**
 * Una sola sessione attiva per tipo di dispositivo (computer / smartphone) per conto,
 * insieme a viewer FIVL e sito nuovo. Regole sul server: cumulus-projects/php/rasp_sessione.php.
 *
 * Chiede ad app_sessione.php all'avvio, ogni minuto e al ritorno in primo piano, solo
 * mentre l'app e' visibile (un telefono a schermo spento non tiene il posto). Se il posto
 * e' di un altro dispositivo copre l'app con l'avviso e "Usa qui", che lo riprende.
 * Nessuna disconnessione: la chiave resta valida. Errori di rete: non si ferma niente.
 */
const INTERVAL = 60000;

export const SessionGuard: React.FC = () => {
  const { t } = useI18n();
  const [other, setOther] = useState<SessionCheck['altrove'] | null>(null);
  const [busy, setBusy] = useState(false);
  const blocked = useRef(false);
  const running = useRef(false);

  const check = useCallback(async () => {
    if (blocked.current || running.current || document.visibilityState !== 'visible') return;
    running.current = true;
    const r = await apiClient.checkSession(false);
    running.current = false;
    if (r && r.ok === false) {
      blocked.current = true;
      setOther(r.altrove || { classe: 'm', client: '', disp: '' });
    }
  }, []);

  useEffect(() => {
    check();
    const id = window.setInterval(check, INTERVAL);
    const onVis = () => { check(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [check]);

  const takeOver = async () => {
    setBusy(true);
    const r = await apiClient.checkSession(true);
    setBusy(false);
    if (!r || r.ok !== false) {
      blocked.current = false;
      setOther(null);
    }
  };

  if (!other) return null;
  const names: Record<string, string> = { viewer: t('sess.viewer') as string, sito: t('sess.site') as string, app: t('sess.app') as string };
  const who = [other.disp, names[other.client] || other.client].filter(Boolean).join(', ');

  return (
    <div className="overlay-backdrop" style={{ zIndex: 4000, alignItems: 'center' }}>
      <div className="overlay announcement-overlay" style={{ maxWidth: '420px' }} role="dialog">
        <div className="overlay-header">
          <h2>{other.classe === 'd' ? t('sess.titleComputer') : t('sess.titlePhone')}</h2>
        </div>
        <div className="overlay-content">
          <p style={{ marginTop: 0, marginBottom: '20px' }}>{t('sess.body', { who })}</p>
          <div style={{ textAlign: 'center' }}>
            <button onClick={takeOver} disabled={busy} className="btn-primary" style={{ padding: '12px 40px', fontSize: '16px', cursor: 'pointer' }}>
              {busy ? t('sess.wait') : t('sess.useHere')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
